import { useEffect, useState } from "react"
import { Button } from "@ui/shad"

import { ApiRequestError } from "@/hooks/api/base"

// Keyed by the backend's structured `error` code. Reauthentication is only ever offered for
// `storage_integration_invalid_auth` — an authorization failure must not read as expired OAuth.
const STORAGE_ERROR_MESSAGES: Record<string, string | undefined> = {
  storage_folder_not_found: "Folder moved or deleted.",
  storage_rate_limited: "Storage temporarily unavailable.",
  storage_folder_unauthorized: "Folder is outside the linked folder.",
  storage_integration_invalid_auth: "Storage needs reauthentication.",
}

export function StorageRequestError({
  error,
  retry,
  errorUpdatedAt = 0,
}: {
  error: Error
  retry: () => void
  errorUpdatedAt?: number
}) {
  const body = error instanceof ApiRequestError ? error.responseBody : undefined
  const retryAfter =
    body && typeof body === "object" && "retry_after" in body && typeof body.retry_after === "number"
      ? body.retry_after
      : 0
  const [now, setNow] = useState(Date.now)
  const remaining = Math.max(0, Math.ceil((errorUpdatedAt + retryAfter * 1000 - now) / 1000))
  useEffect(() => {
    if (!remaining) return
    const timer = setTimeout(() => setNow(Date.now()), 1000)
    return () => clearTimeout(timer)
  }, [remaining])
  const message = (error instanceof ApiRequestError && STORAGE_ERROR_MESSAGES[error.message]) || "Could not load files."
  return (
    <div role="alert" className="text-muted-foreground flex min-h-10 items-center gap-2 p-2 text-sm">
      <span>{message}</span>
      <Button size="xs" variant="outline" onClick={retry} disabled={remaining > 0}>
        Retry{remaining > 0 ? ` in ${remaining}s` : ""}
      </Button>
    </div>
  )
}
